import React from 'react';
import i18n from 'i18n';
import _ from 'lodash';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { renderSpaceNameError, renderStoreNameError } from './SpaceNameError';

export interface CustomInputComponentModel {
  readonly leftDescription: string;
  readonly rightDescription: string;
  readonly inputPlaceholder: string;
  readonly buttonDirection?: 'row' | 'row-reverse';
  readonly event: UpdateEventListener<CustomInputComponentModel>;
  readonly customSelectOptions: SelectOptions[];
  onChange: (value: string) =>
    | {
        error: React.ReactNode;
        invalidValue: string;
      }
    | undefined;
}

export type CustomInputComponentProps = {
  readonly model: CustomInputComponentModel;
};

export type CustomInputComponentState = {
  readonly input: string;
  readonly error?: React.ReactNode;
};

export abstract class AbstractCustomInputComponentModel implements CustomInputComponentModel {
  leftDescription: string;
  rightDescription: string;
  inputPlaceholder: string;
  event: FireableUpdateEventListener<CustomInputComponentModel>;
  customSelectOptions: SelectOptions[];

  constructor(
    leftDescription: string,
    rightDescription: string,
    inputPlaceholder: string,
    public buttonDirection?: 'row' | 'row-reverse',
    protected allOptions?: SelectOptions[]
  ) {
    this.leftDescription = leftDescription;
    this.rightDescription = rightDescription;
    this.inputPlaceholder = inputPlaceholder;
    this.event = new FireableUpdateEventListener<CustomInputComponentModel>();
    this.customSelectOptions = [];
  }

  onChange(input: string):
    | {
        error: React.ReactNode;
        invalidValue: string;
      }
    | undefined {
    const handledInput = input.replace(/\n$/, '').trim();
    if (!handledInput || handledInput === '') {
      return undefined;
    }
    this.customSelectOptions = _.map(
      handledInput.split('\n'),
      (customInput) => {
        let customInputs: string[] = customInput.split(',');
        if (customInputs.length >= 2) {
          return {
            label: _.tail(customInputs)
              .map((item) => item.trim())
              .join(','),
            value: customInputs[0].trim(),
            extra: 'isCustom'
          };
        }

        return {
          label: customInput,
          value: customInput,
          extra: 'isCustom'
        };
      }
    );

    this.event.fireEvent(this);
    return undefined;
  }
}

export class SpaceCustomInputComponentModel extends AbstractCustomInputComponentModel {
  constructor(
    title: string = i18n.t('limitation.addSpace'),
    tip: string = i18n.t('limitation.addSpaceTip'),
    placeholder: string = i18n.t('limitation.placeholders.addSpace'),
    buttonDirection: 'row' | 'row-reverse' = 'row'
  ) {
    super(title, tip, placeholder, buttonDirection);
  }
}

export class DomainCustomInputComponentModel extends AbstractCustomInputComponentModel {
  constructor(
    title: string = i18n.t('limitation.addDomain'),
    tip: string = i18n.t('limitation.addDomainTip'),
    placeholder: string = i18n.t('limitation.placeholders.addDomain'),
    buttonDirection: 'row' | 'row-reverse' = 'row'
  ) {
    super(title, tip, placeholder, buttonDirection);
  }
}

export class MachineCustomInputComponentModel extends AbstractCustomInputComponentModel {
  allOptions: SelectOptions[];

  constructor(
    allOptions: SelectOptions[],
    title: string = i18n.t('limitation.addOutdoorDevice'),
    tip: string = i18n.t('limitation.addOutdoorDeviceTip'),
    placeholder: string = i18n.t('limitation.placeholders.addOutdoorDevice'),
    buttonDirection: 'row' | 'row-reverse' = 'row-reverse'
  ) {
    super(title, tip, placeholder, buttonDirection);
    this.allOptions = allOptions;
  }

  onChange(input: string):
    | {
        error: React.ReactNode;
        invalidValue: string;
      }
    | undefined {
    const handledInput = input.replace(/\n$/, '').trim();
    if (!handledInput || handledInput === '') {
      return undefined;
    }
    const inputNames = handledInput.split('\n');
    let invalidString = '';
    let validOptions: SelectOptions[] = [];
    inputNames.forEach((inputName) => {
      const option = this.allOptions.find(
        (option) => option.label === inputName
      );
      if (option) {
        validOptions.push({
          ...option,
          extra: 'isCustom'
        });
      } else {
        invalidString += inputName + '\n';
      }
    });
    this.customSelectOptions = validOptions;
    this.event.fireEvent(this);

    if (invalidString === '') {
      return undefined;
    }

    return {
      invalidValue: invalidString,
      error: renderSpaceNameError()
    };
  }
}

export class PublisherCustomInputComponentModel extends AbstractCustomInputComponentModel {
  constructor(
    title: string = i18n.t('limitation.addPublisher'),
    tip: string = i18n.t('limitation.addPublisherTip'),
    placeholder: string = i18n.t('limitation.placeholders.addPublisher'),
    buttonDirection: 'row' | 'row-reverse' = 'row'
  ) {
    super(title, tip, placeholder, buttonDirection);
  }
}

export class StoreCustomInputComponentModel extends AbstractCustomInputComponentModel {
  allOptions: SelectOptions[];

  constructor(
    allOptions: SelectOptions[],
    title: string = i18n.t('limitation.addOutdoorStore'),
    tip: string = i18n.t('limitation.addOutdoorStoreTip'),
    placeholder: string = i18n.t('limitation.placeholders.addOutdoorStore'),
    buttonDirection: 'row' | 'row-reverse' = 'row-reverse'
  ) {
    super(title, tip, placeholder, buttonDirection);
    this.allOptions = allOptions;
  }

  onChange(input: string):
    | {
        error: React.ReactNode;
        invalidValue: string;
      }
    | undefined {
    const handledInput = input.replace(/\n$/, '').trim();
    if (!handledInput || handledInput === '') {
      return undefined;
    }
    const inputNames = handledInput.split('\n');
    let invalidString = '';
    let validOptions: SelectOptions[] = [];
    inputNames.forEach((inputName) => {
      const option = this.allOptions.find(
        (option) => option.label === inputName
      );
      if (option) {
        validOptions.push({
          ...option,
          extra: 'isCustom'
        });
      } else {
        invalidString += inputName + '\n';
      }
    });
    this.customSelectOptions = validOptions;
    this.event.fireEvent(this);

    if (invalidString === '') {
      return undefined;
    }

    return {
      invalidValue: invalidString,
      error: renderStoreNameError()
    };
  }
}
